import { create } from "zustand";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { temporal } from "zundo";
import type { TemporalState } from "zundo";
import jp from "jsonpath";
import { EforceIncidentModel } from "../shared/interfaces/EforceModels";

interface FormDataState {
  formData: EforceIncidentModel | null;
  currentSection: number;
  isInitialized: boolean;
  initStore: (
    initialData: EforceIncidentModel | null,
    initialSection?: number
  ) => void;
  destroyStore: () => void;
  updateFieldByPath: (jsonPath: string, value: unknown) => void;
  setFormData: (data: EforceIncidentModel) => void;
  setCurrentSection: (section: number) => void;
  getFieldByPath: (jsonPath: string) => unknown;
}

const initialState: Pick<FormDataState, "formData" | "currentSection" | "isInitialized"> = {
  formData: null,
  currentSection: 0,
  isInitialized: false,
};

export const useFormDataStore = create<FormDataState>()(
  temporal(
    (set, get) => ({
      ...initialState,
      initStore: (initialData, initialSection = 0) => {
        set({
          formData: initialData ? JSON.parse(JSON.stringify(initialData)) : null,
          currentSection: initialSection,
          isInitialized: true,
        });
        useFormDataStore.temporal.getState().clear();
      },
      destroyStore: () => {
        set(initialState);
        useFormDataStore.temporal.getState().clear();
      },
      updateFieldByPath: (jsonPath, value) => {
        set((state) => {
          if (!state.formData) return state;
          const updatedData: EforceIncidentModel = JSON.parse(
            JSON.stringify(state.formData)
          );
          jp.value(updatedData, jsonPath, value);
          return { formData: updatedData };
        });
      },
      setFormData: (data) => {
        set({
          formData: data,
          isInitialized: true,
        });
      },
      setCurrentSection: (section) => set({ currentSection: section }),
      getFieldByPath: (jsonPath) => {
        const { formData } = get();
        if (!formData) return null;
        return jp.value(formData, jsonPath);
      },
    }),
    {
      partialize: (state) => ({
        formData: state.formData,
        currentSection: state.currentSection,
      }),
      equality: (pastState, currentState) =>
        JSON.stringify(pastState.formData) ===
          JSON.stringify(currentState.formData) &&
        pastState.currentSection === currentState.currentSection,
      limit: 50,
    }
  )
);

type FormDataTemporalState = TemporalState<Pick<FormDataState, 'formData' | 'currentSection'>>;

export const useFormDataTemporal = () =>
  useStoreWithEqualityFn(
    useFormDataStore.temporal,
    (state: FormDataTemporalState) => ({
      undo: state.undo,
      redo: state.redo,
      clear: state.clear,
      pastStates: state.pastStates,
      futureStates: state.futureStates,
    }),
    shallow
  );

export const useCanUndo = () =>
  useStoreWithEqualityFn(
    useFormDataStore.temporal,
    (state: FormDataTemporalState) => state.pastStates.length > 0
  );

export const useCanRedo = () =>
  useStoreWithEqualityFn(
    useFormDataStore.temporal,
    (state: FormDataTemporalState) => state.futureStates.length > 0
  );
